import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { isBankStatementItem } from "../lib/ownership";
import { toast } from "sonner";
import {
  FileText, MagnifyingGlass, Trash, Eye, LinkSimple,
  PaperclipHorizontal, Folder, CheckCircle, CaretDown, ChatCircleDots,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import PageHelpButton from "../components/PageHelpButton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import DocumentViewer from "../components/DocumentViewer";
import DirectedToPicker from "../components/DirectedToPicker";

const CATEGORY_LABEL = {
  remittance: "Remittance",
  statement: "Statement",
  inquiry: "Inquiry",
  other: "Other",
};

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Files() {
  const { activeCompanyId, activeCompany } = useCompany();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | unlinked | remittance | statement | other
  const [previewing, setPreviewing] = useState(null);
  const [attachOpen, setAttachOpen] = useState(null); // { doc, items: [] }
  const [attachItem, setAttachItem] = useState("");
  const [attachLoading, setAttachLoading] = useState(false);
  // "Ask about this file" — opens an inquiry pre-filled with the doc identity.
  const [askDoc, setAskDoc] = useState(null);
  const [askTitle, setAskTitle] = useState("");
  const [askBody, setAskBody] = useState("");
  const [askSubmitting, setAskSubmitting] = useState(false);
  const defaultDirectedTo =
    user?.role === "owner" ? "bookkeeper" :
    user?.role === "bookkeeper" ? "owner" :
    "bookkeeper";
  const [askDirectedTo, setAskDirectedTo] = useState(defaultDirectedTo);

  const openAskDialog = (doc) => {
    setAskDoc(doc);
    const fname = doc.display_filename || doc.original_filename;
    setAskTitle(`Re: ${fname}`);
    setAskBody("");
    setAskDirectedTo(defaultDirectedTo);
  };
  const submitAsk = async () => {
    if (!askDoc || !askTitle.trim()) return;
    setAskSubmitting(true);
    try {
      const fname = askDoc.display_filename || askDoc.original_filename;
      const linkBits = [
        `Regarding the file: ${fname}`,
        askDoc.monthly_item ? `linked to "${askDoc.monthly_item.name}" (${askDoc.monthly_item.month})` : null,
        askDoc.uploaded_by_name ? `uploaded by ${askDoc.uploaded_by_name}` : null,
      ].filter(Boolean).join(" · ");
      const fullBody = askBody.trim() ? `${linkBits}\n\n${askBody.trim()}` : linkBits;
      await api.post("/questions", {
        company_id: activeCompanyId,
        title: askTitle.trim(),
        body: fullBody,
        due_date: null,
        linked_document_id: askDoc.id,
        directed_to: askDirectedTo || null,
      });
      toast.success("Inquiry submitted");
      setAskDoc(null);
      setAskTitle("");
      setAskBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit inquiry");
    }
    setAskSubmitting(false);
  };

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: activeCompanyId });
      if (filter === "unlinked") params.set("unlinked_only", "true");
      else if (filter !== "all") params.set("category", filter);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/documents?${params.toString()}`);
      setDocs(data);
    } catch (e) {
      console.warn("Failed to load documents", e);
    }
    setLoading(false);
  }, [activeCompanyId, filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (doc) => {
    const linked = doc.monthly_item ? `\n\nThis file is currently marking "${doc.monthly_item.name}" complete for ${doc.monthly_item.month}. Deleting will reset that item to pending.` : "";
    if (!window.confirm(`Permanently delete "${doc.display_filename || doc.original_filename}"?${linked}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("File deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  // Promote any file on the Files page to a Remittances row. Use case: photo
  // ended up here by mistake (or upload routed wrong), and the owner wants
  // it on Remittances with AI extraction applied.
  const handleSendToRemittances = async (doc) => {
    if (!window.confirm(
      `Send "${doc.display_filename || doc.original_filename}" to the Remittances page?\n\n` +
      `It will be processed as a payment advice with AI extraction (payer / amount / invoice #).`
    )) return;
    try {
      const fd = new FormData();
      fd.append("document_id", doc.id);
      const { data } = await api.post("/payment-advices/from-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fields = [];
      if (data?.payer_name) fields.push(data.payer_name);
      if (data?.amount) fields.push(`$${Number(data.amount).toFixed(2)}`);
      if (data?.invoice_number) fields.push(data.invoice_number);
      toast.success(
        fields.length > 0
          ? `Sent to Remittances — ${fields.join(" · ")}`
          : "Sent to Remittances — open the page to fill in fields",
        { duration: 6000 }
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
  };



  const openAttach = async (doc) => {
    try {
      const { data } = await api.get(`/monthly-items?company_id=${activeCompanyId}`);
      // Hide bank-statement rows from the picker — those live on Remittances.
      const pending = data.filter((i) => i.status === "pending" && i.category !== "decision" && !isBankStatementItem(i));
      setAttachOpen({ doc, items: pending });
      setAttachItem("");
    } catch (e) {
      toast.error("Failed to load monthly items");
    }
  };

  const submitAttach = async () => {
    if (!attachOpen || !attachItem) return;
    setAttachLoading(true);
    try {
      await api.post(`/monthly-items/${attachItem}/attach-document`, { document_id: attachOpen.doc.id });
      toast.success("Attached");
      setAttachOpen(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach");
    }
    setAttachLoading(false);
  };

  // "Unlinked" only counts files that genuinely SHOULD be attached to something —
  // statements / receipts / other uploads. Payment advices live on the Remittances
  // page and inquiry attachments live on Inquiries, so they're never "unlinked".
  const orphans = useMemo(
    () => docs.filter((d) =>
      !d.monthly_item_id &&
      !d.question_id &&
      d.category !== "payment_advice"
    ).length,
    [docs],
  );

  return (
    <div data-testid="files-page" className="p-6 sm:p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="label-tag mb-2 flex items-center gap-2">
          <span>Files</span>
          <PageHelpButton
            testId="files-help"
            title="How the file library works"
            bullets={[
              <>This page lists <b>every</b> file ever uploaded for this company — statements, remittances, inquiry attachments.</>,
              <>Files arrive here by being uploaded on <b>Monthly Items</b>, <b>Remittances</b>, or <b>Inquiries</b> — there is no upload button here on purpose.</>,
              <>Use the <b>filter dropdown</b> to see only Statements, only Remittances, or <b>Not attached</b> (statements/receipts that still need to be attached to a monthly item).</>,
              <>Click <b>View</b> to preview, <b>Attach</b> to attach a file to a monthly item, or <b>Delete</b> to remove. Remittance rows live on the <b>Remittances</b> page, not here.</>,
            ]}
            footer={<>Tip: bookkeeper can sweep <b>Not attached</b> at month-end to make sure no statement fell through the cracks.</>}
          />
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
          All your documents.
        </h1>
        <p className="text-[#525252] mt-3 max-w-2xl">
          {activeCompany?.name}. Every PDF, image, and statement uploaded — searchable and re-attachable.
          Remittance advices appear here too, but the day-to-day workflow lives on the <Link to="/payment-advices" className="underline">Remittances</Link> page.
        </p>
      </div>

      {/* Toolbar */}
      <div className="surface-card bg-white p-4 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search filename, uploader, or words inside the file…"
              className="w-full pl-9 pr-3 py-2 border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7] text-sm"
              data-testid="files-search"
            />
          </div>
          {/* Inline pill chips — one click instead of "click dropdown, click option". */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="files-filter-pills">
            {[
              { v: "all", label: "All" },
              { v: "unlinked", label: "Not attached" },
              { v: "statement", label: "Statements" },
              { v: "remittance", label: "Remittances" },
              { v: "other", label: "Other" },
            ].map((c) => (
              <button
                key={c.v}
                onClick={() => setFilter(c.v)}
                data-testid={`files-filter-${c.v}`}
                className={`px-3 py-1.5 text-xs font-medium border transition-all ${
                  filter === c.v
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-[#525252]">
            {docs.length} files{orphans > 0 ? ` · ${orphans} not attached` : ""}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="surface-card bg-white">
        <div className="hidden md:grid grid-cols-12 px-4 py-3 border-b border-[#E5E5E5] label-tag bg-[#FAFAFA]">
          <div className="col-span-5">Filename</div>
          <div className="col-span-3">Attached to</div>
          <div className="col-span-2">Uploaded</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#525252]">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-10 text-center">
            <Folder size={32} weight="duotone" className="mx-auto text-[#525252] mb-2" />
            <div className="text-sm text-[#525252]">No files match.</div>
          </div>
        ) : (
          docs.map((d) => {
            const isImg = (d.content_type || "").startsWith("image/");
            const linked = !!d.monthly_item_id || !!d.question_id;
            return (
              <div
                key={d.id}
                data-testid={`file-${d.id}`}
                className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#E5E5E5] last:border-b-0 items-center hover:bg-[#FAFAFA] transition-colors"
              >
                <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center ${isImg ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
                    {isImg ? <PaperclipHorizontal size={18} weight="duotone" /> : <FileText size={18} weight="duotone" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => setPreviewing(d)}
                      className="text-left text-sm font-medium hover:text-[#002FA7] hover:underline truncate w-full"
                      data-testid={`file-preview-${d.id}`}
                    >
                      {d.display_filename || d.original_filename}
                    </button>
                    <div className="text-[11px] text-[#525252] flex items-center gap-2 flex-wrap">
                      {d.category && <span className="label-tag bg-[#F5F5F5] px-1.5">{CATEGORY_LABEL[d.category] || d.category}</span>}
                      {d.size != null && <span>{formatBytes(d.size)}</span>}
                      {d.uploaded_by_name && <span>by {d.uploaded_by_name}</span>}
                    </div>
                  </div>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm">
                  {d.monthly_item ? (
                    <div>
                      <div className="text-[#10B981] flex items-center gap-1 text-xs font-semibold">
                        <CheckCircle size={12} weight="fill" /> Attached
                      </div>
                      <div className="text-[12px] text-[#525252] truncate">
                        {d.monthly_item.name} · {d.monthly_item.month}
                      </div>
                    </div>
                  ) : d.question_id ? (
                    <div className="text-[#525252] text-xs italic">Inquiry attachment</div>
                  ) : d.category === "payment_advice" ? (
                    <div className="text-[#525252] text-xs">
                      {d.demoted_from_advice_at ? (
                        <>
                          <div className="italic">Moved from Remittances</div>
                          <div className="text-[11px] text-[#737373]">
                            on {new Date(d.demoted_from_advice_at).toLocaleDateString()}
                          </div>
                        </>
                      ) : (
                        <span className="italic">Remittance row · see Remittances page</span>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] text-[11px] font-semibold uppercase tracking-wider">
                      Not attached
                    </span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-[#525252]">
                  {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}
                </div>
                <div className="col-span-12 md:col-span-2 flex flex-col items-stretch gap-1 md:items-end">
                  <button
                    onClick={() => setPreviewing(d)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors w-full md:w-[88px] justify-start md:justify-center"
                    title="View file"
                    data-testid={`file-view-${d.id}`}
                  >
                    <span className="status-dot bg-current" /> View
                  </button>
                  <button
                    onClick={() => openAskDialog(d)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors w-full md:w-[88px] justify-start md:justify-center"
                    title="Ask a question about this file"
                    data-testid={`file-ask-${d.id}`}
                  >
                    <span className="status-dot bg-current" /> Ask
                  </button>
                  {!linked && d.category !== "payment_advice" && (
                    <button
                      onClick={() => openAttach(d)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#7C3AED]/10 text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#7C3AED]/30 transition-colors w-full md:w-[88px] justify-start md:justify-center"
                      title="Attach to a monthly item"
                      data-testid={`file-attach-${d.id}`}
                    >
                      <span className="status-dot bg-current" /> Attach
                    </button>
                  )}
                  {d.category !== "payment_advice" && (
                    <button
                      onClick={() => handleSendToRemittances(d)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#10B981]/30 transition-colors w-full md:w-[88px] justify-start md:justify-center"
                      title="Treat this file as a payment advice — sends it to Remittances with AI extraction."
                      data-testid={`file-send-to-remittances-${d.id}`}
                    >
                      <span className="status-dot bg-current" /> To remit
                    </button>
                  )}
                  {(user?.role === "owner" || d.uploaded_by === user?.id) && (
                    <button
                      onClick={() => handleDelete(d)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#DC2626]/10 text-[#DC2626] hover:bg-[#DC2626] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#DC2626]/30 transition-colors w-full md:w-[88px] justify-start md:justify-center"
                      title="Delete file"
                      data-testid={`file-delete-${d.id}`}
                    >
                      <span className="status-dot bg-current" /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Preview viewer with prev/next nav across the current filtered list */}
      <DocumentViewer
        open={!!previewing}
        onClose={() => setPreviewing(null)}
        docs={docs}
        startId={previewing?.id}
      />

      {/* Attach dialog */}
      <Dialog open={!!attachOpen} onOpenChange={(v) => { if (!v) setAttachOpen(null); }}>
        <DialogContent className="rounded-none" data-testid="file-attach-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Attach to a monthly item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-[#F5F5F5] p-3 border-l-2 border-[#002FA7]">
              <div className="label-tag text-[10px] mb-1">File</div>
              <div className="font-medium truncate text-sm">
                {attachOpen?.doc?.display_filename || attachOpen?.doc?.original_filename}
              </div>
            </div>
            <div>
              <label className="label-tag block mb-2">Pick a pending item</label>
              <Select value={attachItem} onValueChange={setAttachItem}>
                <SelectTrigger className="rounded-none" data-testid="attach-item-select">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-[260px]">
                  {(attachOpen?.items || []).length === 0 ? (
                    <div className="px-3 py-2 text-sm text-[#525252]">No pending items in any month.</div>
                  ) : (
                    (attachOpen?.items || []).map((it) => (
                      <SelectItem key={it.id} value={it.id} className="rounded-none">
                        {it.month} · {it.name}{it.vendor ? ` (${it.vendor})` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAttachOpen(null)} className="px-4 py-2 border border-[#E5E5E5]">Cancel</button>
            <button
              onClick={submitAttach}
              disabled={!attachItem || attachLoading}
              className="btn-ikb px-4 py-2 disabled:opacity-60"
              data-testid="attach-submit"
            >
              {attachLoading ? "Attaching…" : "Attach"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ask about this file */}
      <Dialog
        open={!!askDoc}
        onOpenChange={(v) => { if (!v) { setAskDoc(null); setAskTitle(""); setAskBody(""); } }}
      >
        <DialogContent className="rounded-none max-w-lg" data-testid="file-ask-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ChatCircleDots size={20} weight="duotone" className="text-[#002FA7]" />
              Ask about this file
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-[#F5F5F5] p-3 border-l-2 border-[#002FA7]">
              <div className="label-tag text-[10px] mb-1">Linked file</div>
              <div className="font-heading font-medium truncate">
                {askDoc?.display_filename || askDoc?.original_filename}
              </div>
              {askDoc?.monthly_item && (
                <div className="label-tag text-[10px] text-[#525252] mt-0.5 truncate">
                  📎 {askDoc.monthly_item.name} · {askDoc.monthly_item.month}
                </div>
              )}
            </div>
            <div>
              <label className="label-tag block mb-2">Subject</label>
              <input
                value={askTitle}
                onChange={(e) => setAskTitle(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="file-ask-title"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">What do you need?</label>
              <textarea
                value={askBody}
                onChange={(e) => setAskBody(e.target.value)}
                rows={4}
                placeholder="e.g. Can you confirm what this charge is for?"
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none resize-none"
                data-testid="file-ask-body"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Who&rsquo;s this for?</label>
              <DirectedToPicker
                value={askDirectedTo}
                onChange={setAskDirectedTo}
                testid="file-ask-directed"
              />
              <div className="text-[11px] text-[#525252] mt-1">
                Only that person gets the email/in-app notification.
              </div>
            </div>
            <p className="text-[11px] text-[#525252]">
              The inquiry will appear on the Inquiries page with the file details already attached.
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setAskDoc(null); setAskTitle(""); setAskBody(""); }}
              className="px-4 py-2 border border-[#E5E5E5]"
              data-testid="file-ask-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submitAsk}
              disabled={askSubmitting || !askTitle.trim()}
              className="btn-ikb px-4 py-2 disabled:opacity-60"
              data-testid="file-ask-submit"
            >
              {askSubmitting ? "Submitting…" : "Submit inquiry"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
